import React from "react";
import { bool, shape, string } from "prop-types";
import { filterType } from "../types";
import DepositionAnalysis from "./DepositionAnalysis";
import { getFilterQueryObject } from "../lib/maintenance_plan_action";
import AnalysisPage from "./AnalysisPage";
import useAPI from "../hooks/useAPI";
import useBooleanSwitch from "../hooks/useBooleanSwitch";
import useTimestamp from "../hooks/useTimestamp";
import Loader from "./Loader";

const AnalysisPageContainer = ({
  allowMultiplePlans,
  filters,
  isReadOnly,
  isRecalculating,
  plan,
  includeVAT,
  sustainabilityEnabled,
  budgetGraphEnabled,
  budgetIncludesVat,
}) => {
  const [timestamp, touchTimestamp] = useTimestamp();
  const { data } = useAPI("/maintenance_plan_actions", {
    query: {
      ...getFilterQueryObject({ ...filters, text: "" }),
      include_vat: includeVAT,
      page: { limit: 0 },
    },
    lastUpdate: timestamp,
  });

  useBooleanSwitch(isRecalculating, { offCallback: touchTimestamp });

  if (!data) {
    return <Loader />;
  }

  const {
    building_share,
    facility_share,
    category_share,
    average_cost_per_year_and_area,
    status_share,
    total_cost,
    maintenance_debt,
    average_maintenance_debt_per_area,
    benchmark_maintenance_debt_per_area,
    investment_cost,
    maintenance_cost,
    total_energy_savings,
    total_electricity_savings,
    total_heating_savings,
  } = data.meta.analysis;

  return (
    <React.Fragment>
      <AnalysisPage
        allowMultiplePlans={allowMultiplePlans}
        buildingShare={building_share}
        facilityShare={facility_share}
        categoryShare={category_share}
        costPerYearAndArea={average_cost_per_year_and_area}
        statusShare={status_share}
        yearlyAverageCost={data.meta.yearly_average_cost}
        yearlyCosts={data.meta.yearly_costs_by}
        yearlyNetCosts={data.meta.yearly_net_costs}
        yearlyCostsByActionType={data.meta.yearly_costs_by_action_type}
        yearlyCostsByBudgetStatus={data.meta.yearly_costs_by_budget_status}
        yearlyBudgetCaps={data.meta.yearly_budget_caps}
        yearlyEnergySavings={data.meta.yearly_energy_savings}
        totalCost={total_cost}
        maintenanceDebt={maintenance_debt}
        averageMaintenanceDebtPerArea={average_maintenance_debt_per_area}
        benchmarkMaintenanceDebtPerArea={benchmark_maintenance_debt_per_area}
        investmentCost={investment_cost}
        maintenanceCost={maintenance_cost}
        totalEnergySavings={total_energy_savings}
        totalElectricitySavings={total_electricity_savings}
        totalHeatingSavings={total_heating_savings}
        sustainabilityEnabled={sustainabilityEnabled}
        budgetGraphEnabled={budgetGraphEnabled}
        budgetIncludesVat={budgetIncludesVat}
      />
      {plan && plan.attributes.settings.show_deposition && (
        <>
          <hr />
          <DepositionAnalysis
            isReadOnly={isReadOnly}
            plan={plan}
            isRecalculating={isRecalculating}
            includeVAT={includeVAT}
          />
        </>
      )}
    </React.Fragment>
  );
};

const createConditionalRender = (conditionFn, elementTrue, elementFalse) => {
  return (props) => {
    if (conditionFn(props)) {
      return elementTrue(props);
    } else {
      return elementFalse(props);
    }
  };
};

export default createConditionalRender(
  (props) => Boolean(props.filters),
  AnalysisPageContainer,
  Loader
);

AnalysisPageContainer.propTypes = {
  allowMultiplePlans: bool.isRequired,
  filters: filterType.isRequired,
  isReadOnly: bool.isRequired,
  isRecalculating: bool.isRequired,
  plan: shape({ id: string }),
  sustainabilityEnabled: bool.isRequired,
  budgetGraphEnabled: bool.isRequired,
  budgetIncludesVat: bool.isRequired,
};
